import { Directive, OnInit, Input, ElementRef, HostListener, Optional } from "@angular/core";
import { NgControl } from "@angular/forms";

@Directive({
  selector: '[appNumericInput]',
})
export class NumericInputDirective implements OnInit {
    @Input() maxDecimals: number = 2;
    @Input() minValue: number = 0;
    @Input() maxValue: number = 9999;
    @Input() allowNegative: boolean = false;
    @Input() step: number = 0.01;
    @Input() maxDigits: number = 6; // New input for maximum digits
  
    private element: HTMLInputElement;
  
    constructor(
      private el: ElementRef,
      @Optional() private ngControl: NgControl
    ) {
      this.element = this.el.nativeElement;
    }
  
    ngOnInit() {
      // Don't change the input type, keep it as text
      // Only set step if it's a number input
      if (this.element.type === 'number') {
        this.element.step = this.step.toString();
        this.element.min = this.minValue.toString();
        this.element.max = this.maxValue.toString();
      }
    }
  
    @HostListener('input', ['$event'])
    onInput(event: Event): void {
      const target = event.target as HTMLInputElement;
      let value = target.value;
  
      // Handle decimal-first input (e.g., .25 -> 0.25)
      if (value.startsWith('.')) {
        value = '0' + value;
        target.value = value;
      }
  
      // Remove leading zeros (but allow single zero and decimal-first numbers)
      if (/^0[0-9]+/.test(value) && !value.startsWith('0.')) {
        value = value.replace(/^0+/, '');
        target.value = value;
      }
  
      // Handle decimal restriction
      if (value.includes('.')) {
        const [intPart, decPart] = value.split('.');
        if (decPart.length > this.maxDecimals) {
          value = intPart + '.' + decPart.slice(0, this.maxDecimals);
          target.value = value;
        }
      }
  
      // Check total digits (excluding decimal point) - respect maxlength attribute
      const maxLength = parseInt(target.getAttribute('maxlength') || '0');
      if (maxLength > 0) {
        const digitsOnly = value.replace('.', '');
        if (digitsOnly.length > maxLength) {
          // Truncate to max length
          if (value.includes('.')) {
            const [intPart, decPart] = value.split('.');
            const maxIntDigits = maxLength - this.maxDecimals;
            if (intPart.length > maxIntDigits) {
              value = intPart.slice(0, maxIntDigits) + '.' + decPart;
            }
          } else {
            value = value.slice(0, maxLength);
          }
          target.value = value;
        }
      } else {
        // Use maxDigits if no maxlength attribute
        const digitsOnly = value.replace('.', '');
        if (digitsOnly.length > this.maxDigits) {
          // Truncate to max digits
          if (value.includes('.')) {
            const [intPart, decPart] = value.split('.');
            const maxIntDigits = this.maxDigits - this.maxDecimals;
            if (intPart.length > maxIntDigits) {
              value = intPart.slice(0, maxIntDigits) + '.' + decPart;
            }
          } else {
            value = value.slice(0, this.maxDigits);
          }
          target.value = value;
        }
      }
  
      // Validate range - prevent exceeding maxValue
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        if (numValue < this.minValue) {
          target.value = this.minValue.toString();
          this.updateFormControl(this.minValue.toString());
        } else if (numValue > this.maxValue) {
          // Prevent the input by not updating the value
          // The keydown handler should prevent this from happening in the first place
          return;
        } else {
          this.updateFormControl(value);
        }
      } else {
        this.updateFormControl(value);
      }
    }
  
    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
      event.preventDefault();
      let pastedText = event.clipboardData?.getData('text/plain') || '';
      
      // Handle decimal-first input for pasted content (e.g., .25 -> 0.25)
      if (pastedText.startsWith('.')) {
        pastedText = '0' + pastedText;
      }
      
      // Validate pasted content
      const isValid = this.validatePastedContent(pastedText);
      if (isValid) {
        const target = event.target as HTMLInputElement;
        target.value = pastedText;
        this.updateFormControl(pastedText);
      }
    }
  
    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
      const target = event.target as HTMLInputElement;
      const value = target.value;
      const cursorPosition = target.selectionStart || 0;
  
      // Allow navigation keys
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Enter', 'Backspace', 'Delete'].includes(event.key)) {
        return;
      }
  
      // Allow decimal point only once
      if (event.key === '.' && !value.includes('.')) {
        return;
      }
  
      // Allow negative sign if configured
      if (event.key === '-' && this.allowNegative && cursorPosition === 0) {
        return;
      }
  
      // Only allow numbers
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
        return;
      }
  
      // Check if adding this digit would exceed maxValue
      const testValue = value.slice(0, cursorPosition) + event.key + value.slice(cursorPosition);
      const numTestValue = parseFloat(testValue);
      
      if (!isNaN(numTestValue) && numTestValue > this.maxValue) {
        event.preventDefault();
        return;
      }
  
      // Check maxlength or maxDigits limit
      const maxLength = parseInt(target.getAttribute('maxlength') || '0');
      const effectiveMaxLength = maxLength > 0 ? maxLength : this.maxDigits;
      
      const digitsOnly = value.replace('.', '').replace('-', '');
      if (digitsOnly.length >= effectiveMaxLength && !value.includes('.') && cursorPosition < value.length) {
        event.preventDefault();
        return;
      }
  
      // Check decimal places
      if (value.includes('.')) {
        const [intPart, decPart] = value.split('.');
        const decimalPosition = value.indexOf('.');
        
        if (cursorPosition > decimalPosition && decPart.length >= this.maxDecimals) {
          event.preventDefault();
          return;
        }
      }
    }
  
    private validatePastedContent(content: string): boolean {
      const regex = this.allowNegative 
        ? new RegExp(`^-?\\d*\\.?\\d{0,${this.maxDecimals}}$`)
        : new RegExp(`^\\d*\\.?\\d{0,${this.maxDecimals}}$`);
      
      if (!regex.test(content)) {
        return false;
      }
      
      // Check total digits
      const digitsOnly = content.replace('.', '').replace('-', '');
      return digitsOnly.length <= this.maxDigits;
    }
  
    private updateFormControl(value: string): void {
      // Only update if ngControl exists and has a control property
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(value, { emitEvent: false });
      }
    }
  } 